import { type ReactNode } from "react";

import { cx } from "./cx";
import { CharacterCount, FieldLabel, useFormField, type FieldControlProps } from "./Form";

/**
 * Label + control + hint/error. The control receives its props (id, name, required, aria-*) via render
 * prop. Inside a `<Form>`, the field shows the Form's message for its control (client checks, or the
 * server's `fieldErrors` for `name`); an explicit `error` wins.
 */
export function Field({
  label,
  hint,
  error,
  name,
  required,
  optional,
  characters,
  size = "md",
  id,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string;
  /** The control's `name`: server field errors with this name land here. */
  name?: string;
  /** Marks the label with "*" and makes the control `required`. */
  required?: boolean;
  /** Marks the label "(optional)". */
  optional?: boolean;
  /** Shows "N characters left" for a length-limited control. */
  characters?: { length: number; max: number };
  /** `lg` is the redesigned forms' label (13px, semibold) and message colours, in a flexible column. */
  size?: "md" | "lg";
  /** A stable id for the control, instead of a generated one — e.g. so an `ErrorSummary` can link to it. */
  id?: string;
  children: (props: FieldControlProps) => ReactNode;
}) {
  const {
    id: controlId,
    message,
    messageId,
    countId,
    control,
  } = useFormField({
    label,
    name,
    error,
    required,
    hasHint: Boolean(hint),
    hasCount: Boolean(characters),
    id,
  });
  const lg = size === "lg";
  return (
    <div className={lg ? "flex min-w-0 flex-1 flex-col gap-1.5" : "space-y-1.5"}>
      <FieldLabel
        htmlFor={controlId}
        required={required}
        className={lg ? "text-[13px] font-semibold text-[#5c5551]" : "block text-sm font-medium text-fg"}
      >
        {label}
        {optional && <span className="ml-1.5 font-normal text-fg-subtle">(optional)</span>}
      </FieldLabel>
      {children(control)}
      {(message || hint) && (
        <p
          id={messageId}
          className={cx("text-xs", message ? (lg ? "text-[#b52522]" : "text-red-600") : "text-fg-subtle")}
        >
          {message ?? hint}
        </p>
      )}
      {characters && <CharacterCount id={countId} {...characters} />}
    </div>
  );
}
